import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import { useAppErrorToast } from '@/hooks/use-app-error-toast';
import { useToast } from '@/hooks/use-toast';
import { applyFormErrorDetails } from '@/lib/form-errors';
import { userService } from '@/services/user';
import { UserListItem } from '@/types/user';
import {
  getAccessRole,
  getAssignableCollaboratorRoles,
  getLinkedPartners,
  getUserAccountType,
  isBrandScopedUser,
  resolveBrandOptions,
  toApiUserRole,
} from '@/utils/rbac';
import { AccessProfileSelect } from './components/NewUser/components/AccessProfileSelect';
import { LinkedBrandsField } from './components/NewUser/components/LinkedBrandsField';
import { UserTypeToggle } from './components/NewUser/components/UserTypeToggle';
import { WhatsAppField } from './components/NewUser/components/WhatsAppField';
import {
  CLIENT_TYPE_HINT,
  COLLABORATOR_TYPE_HINT,
  formatWhatsappPayload,
  newUserSchema,
  type NewUserFormValues,
} from './components/NewUser/utils/schema';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserListItem | null;
}

const toWhatsappInputValue = (whatsapp?: string | null): string => {
  const digits = String(whatsapp || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('55') && digits.length > 11) return digits.slice(2);
  return digits;
};

export const EditUserModal = ({ isOpen, onClose, onSuccess, user }: EditUserModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { showErrorToast } = useAppErrorToast();
  const { user: currentUser } = useAuth();
  const { partners: catalogPartners } = useData();
  const assignableRoles = getAssignableCollaboratorRoles(currentUser);
  const canSelectAllBrands = !isBrandScopedUser(currentUser);
  const partners = useMemo(
    () => resolveBrandOptions(currentUser, catalogPartners),
    [catalogPartners, currentUser]
  );

  const form = useForm<NewUserFormValues>({
    resolver: zodResolver(newUserSchema),
    mode: 'onChange',
    defaultValues: {
      accountType: 'COLLABORATOR',
      name: '',
      email: '',
      whatsapp: '',
      role: '',
      partnerIds: [],
      allPartners: false,
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError: setFieldError,
    formState: { errors, isDirty, isValid },
  } = form;

  const accountType = watch('accountType');
  const partnerIds = watch('partnerIds');
  const allPartners = watch('allPartners');
  const selectedRole = watch('role');
  const typeHint = accountType === 'CLIENT' ? CLIENT_TYPE_HINT : COLLABORATOR_TYPE_HINT;

  useEffect(() => {
    if (!user || !isOpen) return;
    const account = getUserAccountType(user);
    reset({
      accountType: account,
      name: user.name || '',
      email: user.email,
      whatsapp: toWhatsappInputValue(user.whatsapp),
      role: account === 'COLLABORATOR' ? getAccessRole(user) : '',
      partnerIds: getLinkedPartners(user).map((partner) => partner.id),
      allPartners: Boolean(user.all_partners),
    });
    setError(null);
  }, [user, isOpen, reset]);

  const togglePartner = (partnerId: string) => {
    if (accountType === 'CLIENT') {
      setValue('partnerIds', [partnerId], { shouldDirty: true, shouldValidate: true });
      setValue('allPartners', false, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const selected = partnerIds.includes(partnerId)
      ? partnerIds.filter((id) => id !== partnerId)
      : [...partnerIds, partnerId];
    setValue('partnerIds', selected, { shouldDirty: true, shouldValidate: true });
    setValue(
      'allPartners',
      canSelectAllBrands && selected.length === partners.length && partners.length > 0,
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const toggleAllPartners = () => {
    if (!canSelectAllBrands) return;
    if (allPartners || partnerIds.length === partners.length) {
      setValue('allPartners', false, { shouldDirty: true, shouldValidate: true });
      setValue('partnerIds', [], { shouldDirty: true, shouldValidate: true });
      return;
    }
    setValue(
      'partnerIds',
      partners.map((partner) => partner.id),
      { shouldDirty: true, shouldValidate: true }
    );
    setValue('allPartners', true, { shouldDirty: true, shouldValidate: true });
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!user) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await userService.updateUser(user.id, {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        whatsapp: formatWhatsappPayload(values.whatsapp),
        ...(values.accountType === 'COLLABORATOR'
          ? {
              role: toApiUserRole(values.role),
              partner_ids: values.allPartners ? undefined : values.partnerIds,
              all_partners: values.allPartners,
            }
          : {
              partner_ids: values.partnerIds,
              partner_id: values.partnerIds[0],
              all_partners: false,
            }),
      });
      toast({
        title: 'Usuário atualizado com sucesso!',
        description: `Os dados de ${values.name.trim()} foram atualizados`,
      });
      onSuccess();
      onClose();
    } catch (submitError: unknown) {
      const normalizedError = showErrorToast(submitError, {
        title: 'Erro ao atualizar usuário',
        fallbackMessage: 'Não foi possível atualizar o usuário.',
      });
      setError(normalizedError.message);
      applyFormErrorDetails(normalizedError.details, setFieldError);
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? handleClose() : undefined)}>
      <DialogPortal>
        <DialogOverlay className="bg-black/40 backdrop-blur-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:duration-300 data-[state=closed]:duration-200" />
        <DialogPrimitive.Content className="fixed inset-y-0 right-0 z-50 flex h-[100dvh] w-full max-w-[min(420px,100vw)] min-w-0 flex-col overflow-hidden border-l bg-white shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full data-[state=open]:duration-300 data-[state=closed]:duration-300 data-[state=open]:ease-out data-[state=closed]:ease-in">
          <div className="flex shrink-0 items-center justify-between px-6 py-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">Editar usuário</DialogTitle>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-sm p-1 text-gray-700 opacity-70 hover:opacity-100"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
          {user ? (
            <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-5 overflow-y-auto px-6 pb-5">
                {error ? (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                ) : null}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-900">Tipo de usuário</p>
                  <UserTypeToggle
                    value={accountType}
                    onChange={() => undefined}
                    disabled
                  />
                  <p className="text-sm text-gray-500">{typeHint}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-900">
                    Nome completo <span className="text-red-500">*</span>
                  </label>
                  <Input
                    {...register('name')}
                    placeholder="Digite o nome completo"
                    className={errors.name ? 'border-red-500' : ''}
                    error={errors.name?.message}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-900">
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    {...register('email')}
                    placeholder="Digite o e-mail"
                    className={errors.email ? 'border-red-500' : ''}
                    error={errors.email?.message}
                  />
                </div>
                <WhatsAppField {...register('whatsapp')} error={errors.whatsapp?.message} />
                {accountType === 'COLLABORATOR' ? (
                  <AccessProfileSelect
                    value={selectedRole}
                    options={assignableRoles}
                    error={errors.role?.message}
                    onChange={(role) =>
                      setValue('role', role, { shouldDirty: true, shouldValidate: true })
                    }
                  />
                ) : null}
                <div className="space-y-3 border-t pt-5">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Marcas vinculadas</p>
                    <p className="text-sm text-gray-500">
                      {accountType === 'CLIENT'
                        ? 'Selecione a marca'
                        : 'Selecione quais marcas o colaborador deverá ser vinculado.'}
                    </p>
                  </div>
                  <LinkedBrandsField
                    partners={partners}
                    selectedIds={partnerIds}
                    allSelected={allPartners}
                    multiple={accountType === 'COLLABORATOR'}
                    allowSelectAll={canSelectAllBrands}
                    error={errors.partnerIds?.message}
                    onToggle={togglePartner}
                    onToggleAll={toggleAllPartners}
                  />
                </div>
              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="dark"
                  disabled={isSubmitting || !isDirty || !isValid}
                  className="min-w-[96px] gap-2"
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Salvar
                </Button>
              </div>
            </form>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
